import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from "@mui/material";
import {
  CheckCircleOutline as ResolveIcon,
} from "@mui/icons-material";
import type { SelectChangeEvent } from "@mui/material";
import { differenceInHours, differenceInDays } from "date-fns";
import { useOutlookPlugin } from "../../hooks/useOutlookPlugin";
import type {
  ReminderWithAction,
} from "@services/emailAutomationPipeline/types";
import { ActionCard } from "./ActionCard";
import { EmailPreview } from "./EmailPreview";
import { useShipmentActions } from "./useShipmentActions";

interface AllActionsComponentProps {
  onGoToShipment: (shipmentId: string) => void;
  reminders: ReminderWithAction[];
  setReminders: React.Dispatch<React.SetStateAction<ReminderWithAction[]>>;
  reminderTypes: { type: string; name: string; count: number }[];
  loading: boolean;
  onRefetch: (params?: { types?: string[]; showResolved?: boolean }) => void;
}

type DateSection =
  | { type: "reminders"; reminders: ReminderWithAction[] }
  | { type: "separator"; label: string };

export const AllActionsComponent: React.FC<AllActionsComponentProps> = ({
  onGoToShipment,
  reminders,
  setReminders,
  reminderTypes,
  loading,
  onRefetch,
}) => {
  const { apiClient } = useOutlookPlugin();
  const [filterTypes, setFilterTypes] = useState<string[]>([]);
  const [showResolved, setShowResolved] = useState(false);

  const {
    draftPreview,
    setDraftPreview,
    draftPreviewAction,
    handleExecute,
    handleSend,
    handleAddAttachment,
    handleRemoveAttachment,
    handleResolve,
  } = useShipmentActions(apiClient, setReminders, { removeOnComplete: true });

  // Re-fetch when filters change
  useEffect(() => {
    onRefetch({ types: filterTypes, showResolved });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterTypes, showResolved]);

  const totalCount = reminderTypes.reduce((sum, rt) => sum + rt.count, 0);

  const handleFilterChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    if (Array.isArray(value) && value.includes("__all__")) {
      setFilterTypes([]);
      return;
    }
    setFilterTypes(typeof value === "string" ? value.split(",") : value);
  };

  // Date separator logic
  const sections = useMemo((): DateSection[] => {
    if (reminders.length === 0) return [];

    const now = new Date();
    const result: DateSection[] = [];
    let currentSection: ReminderWithAction[] = [];
    let hasShownOneDaySeparator = false;
    let hasShownOneWeekSeparator = false;

    for (const reminder of reminders) {
      const hoursSince = differenceInHours(
        now,
        new Date(reminder.reminderCreatedAt)
      );
      const daysSince = differenceInDays(
        now,
        new Date(reminder.reminderCreatedAt)
      );

      if (!hasShownOneWeekSeparator && daysSince >= 7) {
        if (currentSection.length > 0) {
          result.push({ type: "reminders", reminders: currentSection });
          currentSection = [];
        }
        result.push({ type: "separator", label: "More than a week ago" });
        hasShownOneWeekSeparator = true;
        hasShownOneDaySeparator = true;
      } else if (!hasShownOneDaySeparator && hoursSince >= 24) {
        if (currentSection.length > 0) {
          result.push({ type: "reminders", reminders: currentSection });
          currentSection = [];
        }
        result.push({ type: "separator", label: "More than one day ago" });
        hasShownOneDaySeparator = true;
      }

      currentSection.push(reminder);
    }

    if (currentSection.length > 0) {
      result.push({ type: "reminders", reminders: currentSection });
    }

    return result;
  }, [reminders]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        px: 1.5,
        pb: 1,
        pt: 1,
        position: "relative",
        flex: 1,
        minHeight: 0,
        overflow: "auto",
      }}
    >
      {/* Filter bar */}
      <Box sx={{ mb: 1.5 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel sx={{ fontSize: "0.75rem" }}>Filter by type</InputLabel>
          <Select
            multiple
            value={filterTypes}
            onChange={handleFilterChange}
            label="Filter by type"
            renderValue={(selected) =>
              (selected as string[])
                .map(
                  (s) => reminderTypes.find((t) => t.type === s)?.name ?? s
                )
                .join(", ")
            }
            sx={{ fontSize: "0.75rem", height: 32 }}
          >
            <MenuItem value="__all__" sx={{ fontSize: "0.75rem", display: "flex", justifyContent: "space-between" }}>
              <span>All</span>
              <Typography
                component="span"
                variant="caption"
                sx={{ ml: 1, color: "text.secondary", fontSize: "0.7rem" }}
              >
                ({totalCount})
              </Typography>
            </MenuItem>
            {reminderTypes.map((rt) => (
              <MenuItem key={rt.type} value={rt.type} sx={{ fontSize: "0.75rem", display: "flex", justifyContent: "space-between" }}>
                <span>{rt.name}</span>
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ ml: 1, color: "text.secondary", fontSize: "0.7rem" }}
                >
                  ({rt.count})
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          size="small"
          startIcon={!showResolved ? <ResolveIcon sx={{ fontSize: "14px !important" }} /> : undefined}
          onClick={() => setShowResolved(!showResolved)}
          sx={{
            textTransform: "none",
            fontSize: "0.75rem",
            color: "#1565C0",
            mt: 0.5,
            px: 0.5,
            display: "flex",
            "& .MuiButton-startIcon": { mr: 0.3 },
          }}
        >
          {showResolved
            ? `← Show Active (${totalCount})`
            : "Show Resolved"}
        </Button>
      </Box>

      {draftPreview && draftPreviewAction && (
        <EmailPreview
          mode="draft"
          action={draftPreviewAction}
          draftMessageId={draftPreview.draftMessageId}
          draftTo={draftPreview.to}
          draftCc={draftPreview.cc}
          draftSubject={draftPreview.subject}
          draftAttachments={draftPreview.attachments}
          missingAttachments={draftPreview.missingAttachments}
          shipmentId={draftPreview.reminder.shipmentId ?? undefined}
          onAddAttachment={(attId) => handleAddAttachment(draftPreview.reminder.actionId, attId)}
          onRemoveAttachment={(attId) => handleRemoveAttachment(draftPreview.reminder.actionId, attId)}
          replyToEmailId={draftPreview.reminder.data.replyToEmailId as string | undefined}
          onSend={() => handleSend(draftPreview.reminder.actionId)}
          onDismiss={() => setDraftPreview(null)}
        />
      )}

      {reminders.length === 0 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {showResolved
              ? "No resolved reminders found."
              : "No actions assigned to you."}
          </Typography>
        </Box>
      )}

      {sections.map((section, idx) => {
        if (section.type === "separator") {
          return (
            <Divider
              key={`sep-${idx}`}
              sx={{ my: 1.5 }}
              textAlign="left"
            >
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontSize: "0.7rem" }}
              >
                {section.label}
              </Typography>
            </Divider>
          );
        }
        return section.reminders.map((reminder) => (
          <ActionCard
            key={reminder.actionId}
            reminder={reminder}
            onExecute={() => handleExecute(reminder)}
            onResolve={reminder.reminderId ? () => handleResolve(reminder) : undefined}
            onGoToShipment={
              reminder.shipmentId
                ? () => onGoToShipment(reminder.shipmentId!)
                : undefined
            }
          />
        ));
      })}
    </Box>
  );
};
